import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Box,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  IconButton,
  Grid,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Close,
  Search as SearchIcon,
  CheckCircle,
  HourglassTop,
  LinkOff,
  ErrorOutline,
  Sync as SyncIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  WarningAmber,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { colors } from '../../resources/theme/colors';
import { sa, PillButton } from '../superAdminUi';
import wondeService from '../../services/api/wondeService';
import {
  WondeConnectionStatus,
  getStatusLabel,
  getStatusColor,
  isApproved,
  isPending,
  canManageSync,
  canRequestConnection,
  canClearConnection,
  WondeSyncScope,
} from '../../utils/wondeConnectionStatus';

const { twilightBlue, hummingBird, bordergrey, softGrey } = colors;

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '94%',
  maxWidth: 680,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
};

const sectionPaper = {
  p: 2.5,
  mb: 2,
  borderRadius: 2,
  border: `1px solid ${bordergrey}`,
  boxShadow: 'none',
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

// Icon + accent colour per status, for the banner.
const statusVisual = (status) => {
  switch (status) {
    case WondeConnectionStatus.Approved:
      return { icon: <CheckCircle />, color: sa.color.teal };
    case WondeConnectionStatus.PendingApproval:
      return { icon: <HourglassTop />, color: '#C77700' };
    case WondeConnectionStatus.Declined:
    case WondeConnectionStatus.Revoked:
      return { icon: <ErrorOutline />, color: sa.color.pink };
    default:
      return { icon: <LinkOff />, color: sa.color.textGray };
  }
};

// Status-specific copy for the "clear connection" confirmation. Approved is the strongest warning
// because it revokes live data access on Wonde.
const clearCopy = (status, schoolName) => {
  const name = schoolName || 'this school';
  switch (status) {
    case WondeConnectionStatus.Approved:
      // Connected & syncing → this genuinely revokes live Wonde access. Strongest warning.
      return {
        action: 'Revoke access',
        warn: true,
        body: `This revokes Welbee's access to ${name} in Wonde and stops all syncing. Data already synced is kept, but no further updates will flow. To reconnect, the school's Wonde administrator must approve a brand-new request.`,
      };
    case WondeConnectionStatus.PendingApproval:
      return {
        action: 'Clear connection',
        warn: true,
        body: `This withdraws the pending request from Wonde, so the school no longer sees it. You can send a fresh request for the correct school afterwards.`,
      };
    default:
      return {
        action: 'Clear connection',
        warn: false,
        body: `This clears the saved Wonde link for ${name} so you can start a fresh connection.`,
      };
  }
};

/**
 * Manage a single school's Wonde connection + sync. One modal, driven by the
 * canonical connection Status:
 *  - Not connected / unmatched / declined / revoked → search & connect (or manual link)
 *  - Pending approval                               → awaiting-Wonde message + recheck
 *  - Approved                                       → sync settings (save) + run + health
 *
 * The banner (status, Wonde ID, editable URN, clear-connection) is pinned above the scroll
 * area so context never scrolls out of view.
 */
const WondeManageModal = ({ open, onClose, schoolId, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [connection, setConnection] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState(null);

  // --- search / connect state ---
  const [urn, setUrn] = useState('');
  const [postcode, setPostcode] = useState('');
  const [establishmentNumber, setEstablishmentNumber] = useState('');
  const [laCode, setLaCode] = useState('');
  const [candidates, setCandidates] = useState(null);
  const [searching, setSearching] = useState(false);
  const [identifyMode, setIdentifyMode] = useState('search'); // 'search' | 'manual'
  const [chosen, setChosen] = useState(null); // selected candidate
  const [manualWondeId, setManualWondeId] = useState('');

  // sync scope chosen at connect time / when running
  const [staffSync, setStaffSync] = useState(false);
  const [studentParentSync, setStudentParentSync] = useState(true);
  const [flashSync, setFlashSync] = useState(false);

  // --- edit URN ---
  const [editingUrn, setEditingUrn] = useState(false);
  const [urnDraft, setUrnDraft] = useState('');
  const [savingUrn, setSavingUrn] = useState(false);

  // --- clear / disconnect ---
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  // --- unsaved sync-settings guard (manage view) ---
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const flashTimer = useRef(null);

  const resetTransient = () => {
    setUrn('');
    setPostcode('');
    setEstablishmentNumber('');
    setLaCode('');
    setCandidates(null);
    setChosen(null);
    setManualWondeId('');
    setIdentifyMode('search');
    setEditingUrn(false);
    setUrnDraft('');
    setConfirmClear(false);
    setFlashSync(false);
    setError(null);
  };

  const load = useCallback(async () => {
    if (!open || !schoolId) return;
    setFetching(true);
    setError(null);
    try {
      const res = await wondeService.getConnection(schoolId);
      const dto = res && (res.data || res.Data);
      if (res && (res.success || res.Success) && dto) {
        setConnection(dto);
        setStaffSync(Boolean(dto.StaffSyncActive));
        setStudentParentSync(Boolean(dto.StudentParentSyncActive));
        if (canManageSync(dto.Status)) {
          const statusRes = await wondeService.getSyncStatus(schoolId);
          const statusDto = statusRes && (statusRes.data || statusRes.Data);
          if (statusDto) setSyncStatus(statusDto);
        }
      } else {
        setError((res && (res.message || res.Message)) || 'Failed to load Wonde connection.');
        setConnection(null);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load Wonde connection.');
      setConnection(null);
    } finally {
      setFetching(false);
    }
  }, [open, schoolId]);

  useEffect(() => {
    if (open) {
      resetTransient();
      setConnection(null);
      setSyncStatus(null);
      load();
    }
  }, [open, load]);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const afterChange = async () => {
    await load();
    if (onChanged) onChanged();
  };

  // Draw the eye to the sync toggles when a request is attempted with nothing selected.
  const triggerFlash = () => {
    setFlashSync(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashSync(false), 1300);
  };

  const ensureSyncChosen = () => {
    if (!staffSync && !studentParentSync) {
      setError('Choose at least one type of data to sync before requesting.');
      triggerFlash();
      return false;
    }
    return true;
  };

  // In the approved "Sync settings" view the toggles are draft state until "Save settings".
  // Dirty = the draft differs from what the server last confirmed, so we can guard against
  // losing unsaved changes (the toggles only ever "reverted" because closing dropped the draft).
  const manageDirty =
    connection != null &&
    isApproved(connection.Status) &&
    (staffSync !== Boolean(connection.StaffSyncActive) ||
      studentParentSync !== Boolean(connection.StudentParentSyncActive));

  const resetSyncDraft = () => {
    setStaffSync(Boolean(connection?.StaffSyncActive));
    setStudentParentSync(Boolean(connection?.StudentParentSyncActive));
  };

  // Every close path (header ✕, footer Close, backdrop, Escape) funnels through here so a dirty
  // manage view always gets the discard prompt first. The button/icon are disabled while a request
  // is in flight, but backdrop-click and Escape aren't — so guard `loading` here too, otherwise a
  // close mid-save could unmount the modal and drop the pending state update.
  const requestClose = () => {
    if (loading) return;
    if (manageDirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const discardAndClose = () => {
    resetSyncDraft();
    setConfirmDiscard(false);
    onClose();
  };

  // ---- search ----
  const handleSearch = async () => {
    const urnTrimmed = urn.trim();
    let parsedUrn = null;
    if (urnTrimmed) {
      if (!/^\d+$/.test(urnTrimmed)) {
        setError('URN must be a number.');
        return;
      }
      parsedUrn = parseInt(urnTrimmed, 10);
    }
    const hasFilter =
      Boolean(parsedUrn) || postcode.trim() || establishmentNumber.trim() || laCode.trim();
    if (!hasFilter) {
      setError('Enter a URN, postcode, establishment number or LA code to search.');
      return;
    }

    setSearching(true);
    setError(null);
    setCandidates(null);
    setChosen(null);
    try {
      const res = await wondeService.search({
        schoolId,
        urn: parsedUrn,
        postcode: postcode.trim(),
        establishmentNumber: establishmentNumber.trim(),
        laCode: laCode.trim(),
      });
      const list = (res && (res.data || res.Data)) || [];
      if (res && (res.success || res.Success)) {
        setCandidates(list);
      } else {
        setError((res && (res.message || res.Message)) || 'Search failed.');
      }
    } catch (e) {
      setError(e?.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  // ---- connect chosen candidate ----
  const handleConnect = async (candidate) => {
    if (!ensureSyncChosen()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await wondeService.connect({
        schoolId,
        wondeSchoolId: candidate.WondeSchoolId,
        urn: candidate.Urn || undefined,
        staffSync,
        studentParentSync,
      });
      if (res && (res.success || res.Success)) {
        toast.success((res.message || res.Message) || 'Connection request sent to Wonde.');
        await afterChange();
      } else {
        setError((res && (res.message || res.Message)) || 'Failed to request connection.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to request connection.');
    } finally {
      setLoading(false);
    }
  };

  // ---- manual link ----
  const handleManualLink = async () => {
    if (!manualWondeId.trim()) {
      setError('Please enter a Wonde school id.');
      return;
    }
    if (!ensureSyncChosen()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await wondeService.link({
        schoolId,
        wondeSchoolId: manualWondeId.trim(),
        staffSync,
        studentParentSync,
      });
      if (res && (res.success || res.Success)) {
        toast.success((res.message || res.Message) || 'Connection request sent to Wonde.');
        await afterChange();
      } else {
        setError((res && (res.message || res.Message)) || 'Failed to link Wonde school.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to link Wonde school.');
    } finally {
      setLoading(false);
    }
  };

  // ---- save sync settings (no run) ----
  const handleSaveSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await wondeService.saveSyncSettings(schoolId, { staffSync, studentParentSync });
      if (res && (res.success || res.Success)) {
        toast.success('Sync settings saved.');
        await afterChange();
      } else {
        setError((res && (res.message || res.Message)) || 'Failed to save sync settings.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to save sync settings.');
    } finally {
      setLoading(false);
    }
  };

  // ---- run sync now ----
  const handleRunSync = async () => {
    setLoading(true);
    setError(null);
    try {
      // Only staff sync can be triggered from here. Student/parent data is moved by the external
      // data pipeline (Airbyte) and can't be run on demand from Admin.
      const res = await wondeService.runSync(schoolId, WondeSyncScope.Staff);
      if (res && (res.success || res.Success)) {
        toast.success((res.message || res.Message) || 'Sync started.');
        await afterChange();
      } else {
        setError((res && (res.message || res.Message)) || 'Failed to start sync.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to start sync.');
    } finally {
      setLoading(false);
    }
  };

  // ---- edit URN ----
  const beginEditUrn = () => {
    setUrnDraft(connection?.Urn ? String(connection.Urn) : '');
    setEditingUrn(true);
    setError(null);
  };

  const handleSaveUrn = async () => {
    const draft = urnDraft.trim();
    if (!/^\d+$/.test(draft) || parseInt(draft, 10) <= 0) {
      setError('URN must be a positive number.');
      return;
    }
    setSavingUrn(true);
    setError(null);
    try {
      const res = await wondeService.updateUrn(schoolId, parseInt(draft, 10));
      if (res && (res.success || res.Success)) {
        toast.success('URN updated.');
        setEditingUrn(false);
        await afterChange();
      } else {
        setError((res && (res.message || res.Message)) || 'Failed to update URN.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to update URN.');
    } finally {
      setSavingUrn(false);
    }
  };

  // ---- clear / disconnect ----
  const handleClearConnection = async () => {
    setClearing(true);
    setError(null);
    try {
      const res = await wondeService.clearConnection(schoolId);
      if (res && (res.success || res.Success)) {
        toast.success((res.message || res.Message) || 'Connection cleared.');
        setConfirmClear(false);
        await afterChange();
      } else {
        setError((res && (res.message || res.Message)) || 'Failed to clear the connection.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to clear the connection.');
    } finally {
      setClearing(false);
    }
  };

  // ---------- render helpers ----------

  const renderUrnField = () => {
    if (editingUrn) {
      return (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" sx={{ color: sa.color.textGray }}>
            URN
          </Typography>
          <TextField
            size="small"
            value={urnDraft}
            onChange={(e) => setUrnDraft(e.target.value)}
            placeholder="e.g. 103603"
            sx={{ width: 110, '& .MuiInputBase-input': { py: 0.25, fontSize: 13 } }}
            autoFocus
            disabled={savingUrn}
          />
          <Tooltip title="Save URN">
            <span>
              <IconButton size="small" onClick={handleSaveUrn} disabled={savingUrn} sx={{ color: sa.color.teal }}>
                {savingUrn ? <CircularProgress size={14} color="inherit" /> : <CheckIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <span>
              <IconButton size="small" onClick={() => setEditingUrn(false)} disabled={savingUrn}>
                <Close fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" sx={{ color: sa.color.textGray }}>
          URN: {connection?.Urn ? connection.Urn : '—'}
        </Typography>
        <Tooltip title="Edit URN">
          <IconButton size="small" onClick={beginEditUrn} sx={{ p: 0.25, color: sa.color.teal }}>
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  const renderBanner = () => {
    if (!connection) return null;
    const v = statusVisual(connection.Status);
    const showClear = canClearConnection(connection.Status);
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 2, bgcolor: hummingBird, display: 'flex', alignItems: 'center', gap: 2 }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            bgcolor: '#fff',
            color: v.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {v.icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: sa.color.text }} noWrap>
              {connection.SchoolName || 'School'}
            </Typography>
            <Chip
              size="small"
              label={connection.StatusLabel || getStatusLabel(connection.Status)}
              color={getStatusColor(connection.Status)}
            />
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: sa.color.textGray }} noWrap>
              {connection.WondeSchoolId ? `Wonde ID: ${connection.WondeSchoolId}` : 'Not yet linked to a Wonde school'}
            </Typography>
            {renderUrnField()}
            {connection.RequestedAtUtc && (
              <Typography variant="caption" sx={{ color: sa.color.textGray }} noWrap>
                Requested {formatDate(connection.RequestedAtUtc)}
              </Typography>
            )}
          </Stack>
        </Box>
        {showClear && (
          <PillButton
            type="button"
            $variant="danger"
            onClick={() => setConfirmClear(true)}
            disabled={loading || clearing}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {clearCopy(connection.Status, connection.SchoolName).action}
          </PillButton>
        )}
      </Paper>
    );
  };

  const syncSetupCallout = (heading) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: `2px solid ${sa.color.teal}`,
        bgcolor: twilightBlue,
        boxShadow: 'none',
        '@keyframes wflash': {
          '0%, 100%': { boxShadow: 'none' },
          '50%': { boxShadow: `0 0 0 4px ${sa.color.pink}` },
        },
        ...(flashSync && { animation: 'wflash 0.45s ease-in-out 2' }),
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: sa.color.text, mb: 1 }}>
        {heading}
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap">
        <FormControlLabel
          control={<Switch checked={staffSync} onChange={() => setStaffSync((s) => !s)} />}
          label="Staff sync"
        />
        <FormControlLabel
          control={<Switch checked={studentParentSync} onChange={() => setStudentParentSync((s) => !s)} />}
          label="Student / parent sync"
        />
      </Stack>
      <Typography variant="caption" sx={{ color: sa.color.textGray, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <WarningAmber sx={{ fontSize: 15 }} /> Turn on at least one before requesting.
      </Typography>
    </Paper>
  );

  const renderConnectFlow = () => (
    <Box>
      {syncSetupCallout('Step 1 — confirm what to sync')}

      <Paper elevation={0} sx={sectionPaper}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: sa.color.text, mb: 1.5 }}>
          Step 2 — identify this school in Wonde
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={identifyMode}
          onChange={(e, v) => { if (v) { setIdentifyMode(v); setError(null); } }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="search" sx={{ textTransform: 'none', px: 2 }}>
            <SearchIcon sx={{ fontSize: 18, mr: 0.75 }} /> Search Wonde
          </ToggleButton>
          <ToggleButton value="manual" sx={{ textTransform: 'none', px: 2 }}>
            I have the Wonde ID
          </ToggleButton>
        </ToggleButtonGroup>

        {identifyMode === 'search' ? (
          <>
            <Typography variant="body2" sx={{ color: sa.color.textGray, mb: 2 }}>
              Search by URN — or, for schools without a URN (e.g. Northern Ireland), by postcode,
              establishment number or LA code.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="URN" value={urn} onChange={(e) => setUrn(e.target.value)} placeholder="e.g. 123456" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="e.g. BT1 1AA" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Establishment number" value={establishmentNumber} onChange={(e) => setEstablishmentNumber(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="LA code" value={laCode} onChange={(e) => setLaCode(e.target.value)} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <PillButton
                type="button"
                $variant="teal"
                $size="lg"
                onClick={handleSearch}
                disabled={searching || (!urn.trim() && !postcode.trim() && !establishmentNumber.trim() && !laCode.trim())}
              >
                {searching ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <>
                    <SearchIcon sx={{ fontSize: 16 }} /> Search Wonde
                  </>
                )}
              </PillButton>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: sa.color.textGray, mb: 2 }}>
              Already have the Wonde school id (e.g. from the Wonde dashboard, or for a school with no URN)?
              Enter it directly and we'll send the access request.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                size="small"
                fullWidth
                label="Wonde school id"
                value={manualWondeId}
                onChange={(e) => setManualWondeId(e.target.value)}
                placeholder="e.g. A1329183376"
              />
              <PillButton
                type="button"
                $variant="teal"
                $size="lg"
                onClick={handleManualLink}
                disabled={loading}
                style={{ whiteSpace: 'nowrap' }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : 'Link & request'}
              </PillButton>
            </Stack>
          </>
        )}
      </Paper>

      {identifyMode === 'search' && candidates && candidates.length > 0 && (
        <Paper elevation={0} sx={sectionPaper}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: sa.color.text, mb: 0.5 }}>
            {candidates.length} match{candidates.length === 1 ? '' : 'es'} found
          </Typography>
          <Typography variant="body2" sx={{ color: sa.color.textGray, mb: 1.5 }}>
            Pick the correct school, then request a connection. Double-check the URN/postcode — sibling schools
            can share a postcode.
          </Typography>
          <Stack spacing={1.25} sx={{ maxHeight: 230, overflowY: 'auto', pr: 0.5 }}>
            {candidates.map((c) => {
              const busy = loading && chosen?.WondeSchoolId === c.WondeSchoolId;
              return (
                <Paper
                  key={c.WondeSchoolId}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${bordergrey}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:hover': { borderColor: sa.color.teal, bgcolor: twilightBlue },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: sa.color.text }} noWrap>
                      {c.Name || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: sa.color.textGray }}>
                      {[c.Urn ? `URN ${c.Urn}` : null, c.Town, c.Postcode, `Wonde ${c.WondeSchoolId}`]
                        .filter(Boolean)
                        .join('  ·  ')}
                    </Typography>
                  </Box>
                  <PillButton
                    type="button"
                    $variant="teal"
                    onClick={() => { setChosen(c); handleConnect(c); }}
                    disabled={loading}
                  >
                    {busy ? <CircularProgress size={16} color="inherit" /> : 'Request'}
                  </PillButton>
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      )}

      {identifyMode === 'search' && candidates && candidates.length === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <PillButton type="button" $variant="outline" onClick={() => setIdentifyMode('manual')}>
              Enter Wonde ID
            </PillButton>
          }
        >
          No schools matched your search in Wonde. If you have the Wonde school id, enter it directly instead.
        </Alert>
      )}
    </Box>
  );

  const renderPending = () => (
    <Paper elevation={0} sx={sectionPaper}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <HourglassTop sx={{ color: '#C77700', mt: 0.25 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: sa.color.text }}>
            Waiting for Wonde to approve
          </Typography>
          <Typography variant="body2" sx={{ color: sa.color.textGray, mt: 0.5 }}>
            Your connection request has been sent. You'll be notified automatically when Wonde approves
            it, and sync options will appear here. If you picked the wrong school, use “Cancel request” above
            to withdraw it and start again.
          </Typography>
          {connection?.RequestedAtUtc && (
            <Typography variant="caption" sx={{ color: sa.color.textGray, display: 'block', mt: 1 }}>
              Requested {formatDate(connection.RequestedAtUtc)}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <PillButton type="button" $variant="teal" $size="lg" onClick={load} disabled={fetching}>
              {fetching ? <CircularProgress size={16} color="inherit" /> : 'Re-check status'}
            </PillButton>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );

  const healthRow = (label, value) => (
    <Grid container sx={{ py: 0.75, borderBottom: `1px solid ${softGrey}` }}>
      <Grid item xs={5} sm={4}>
        <Typography variant="body2" sx={{ color: sa.color.textGray }}>{label}</Typography>
      </Grid>
      <Grid item xs={7} sm={8}>
        <Typography variant="body2" sx={{ color: sa.color.text, wordBreak: 'break-word' }}>{value}</Typography>
      </Grid>
    </Grid>
  );

  const renderManageSync = () => (
    <Box>
      <Paper elevation={0} sx={sectionPaper}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: sa.color.text }}>
          Sync settings
        </Typography>
        <Typography variant="body2" sx={{ color: sa.color.textGray, mb: 2 }}>
          Choose what to keep in sync. Saving settings does not run a sync.
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: manageDirty ? 1 : 2 }}>
          <FormControlLabel
            control={<Switch checked={staffSync} onChange={() => setStaffSync((s) => !s)} />}
            label="Staff sync"
          />
          <FormControlLabel
            control={<Switch checked={studentParentSync} onChange={() => setStudentParentSync((s) => !s)} />}
            label="Student / parent sync"
          />
        </Stack>
        {manageDirty && (
          <Typography
            variant="caption"
            sx={{ color: '#C77700', display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}
          >
            <WarningAmber sx={{ fontSize: 15 }} /> Unsaved changes — click Save settings.
          </Typography>
        )}
        <PillButton
          type="button"
          $variant="teal"
          $size="lg"
          onClick={handleSaveSettings}
          disabled={loading || !manageDirty}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Save settings'}
        </PillButton>
      </Paper>

      <Paper elevation={0} sx={sectionPaper}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: sa.color.text }}>
          Run a staff sync now
        </Typography>
        <Typography variant="body2" sx={{ color: sa.color.textGray, mb: 1 }}>
          Triggers a one-off staff sync using the saved settings. This does not change your settings.
          Student / parent data is moved automatically by the data pipeline and can&apos;t be run from here.
        </Typography>
        {!connection?.StaffSyncActive && (
          <Typography
            variant="caption"
            sx={{ color: '#C77700', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
          >
            <WarningAmber sx={{ fontSize: 15 }} /> Staff sync is off. Turn it on above and Save settings first,
            otherwise a run has nothing to move.
          </Typography>
        )}
        <PillButton type="button" $variant="teal" $size="lg" onClick={handleRunSync} disabled={loading}>
          {loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <>
              <SyncIcon sx={{ fontSize: 16 }} /> Run staff sync now
            </>
          )}
        </PillButton>
      </Paper>

      <Paper elevation={0} sx={sectionPaper}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: sa.color.text, mb: 1 }}>
          Sync health
        </Typography>
        {healthRow('Staff run state', syncStatus?.StaffRunState || 'Idle')}
        {healthRow('Last staff sync', formatDate(connection?.LastStaffSyncUtc))}
        {healthRow(
          'Student / parent pipeline',
          // Only show provisioned/awaiting once the status DTO has a known value; unknown
          // (status not loaded or the BigQuery read failed) shows '—', never a false negative.
          syncStatus?.StudentParentProvisioned == null
            ? '—'
            : syncStatus.StudentParentProvisioned
            ? 'Provisioned'
            : 'Awaiting setup (managed externally)'
        )}
        {healthRow('Last student / parent sync', formatDate(syncStatus?.LastStudentSyncUtc))}
        {healthRow('Last error', connection?.LastSyncError || '—')}
      </Paper>
    </Box>
  );

  const renderBody = () => {
    if (fetching && !connection) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!connection) {
      return <Alert severity="error">{error || 'Unable to load Wonde connection.'}</Alert>;
    }
    const status = connection.Status;
    return (
      <>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {isApproved(status) && renderManageSync()}
        {isPending(status) && renderPending()}
        {canRequestConnection(status) && renderConnectFlow()}
      </>
    );
  };

  const clearCfg = connection ? clearCopy(connection.Status, connection.SchoolName) : null;

  return (
    <Modal open={open} onClose={requestClose} aria-labelledby="wonde-manage-modal-title">
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: `1px solid ${bordergrey}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography id="wonde-manage-modal-title" variant="h6" sx={{ fontWeight: 700, color: sa.color.text }}>
            Wonde connection &amp; sync
          </Typography>
          <Tooltip title="Close">
            <IconButton onClick={requestClose} size="small" disabled={loading}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Pinned banner — stays in view while the body scrolls */}
        {connection && (
          <Box sx={{ px: 3, pt: 2, flexShrink: 0 }}>{renderBanner()}</Box>
        )}

        {/* Scrollable body */}
        <Box sx={{ px: 3, py: 2, overflowY: 'auto', flex: 1 }}>{renderBody()}</Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.75,
            borderTop: `1px solid ${bordergrey}`,
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <PillButton type="button" $variant="outline" $size="lg" onClick={requestClose} disabled={loading}>
            Close
          </PillButton>
        </Box>

        {/* Clear / disconnect confirmation */}
        <Dialog open={confirmClear} onClose={() => !clearing && setConfirmClear(false)} maxWidth="xs">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            {clearCfg?.warn && <WarningAmber sx={{ color: sa.color.pink }} />}
            {clearCfg?.action || 'Clear connection'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: sa.color.text }}>{clearCfg?.body}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmClear(false)} disabled={clearing} sx={{ color: sa.color.textGray }}>
              Keep connection
            </Button>
            <Button
              onClick={handleClearConnection}
              disabled={clearing}
              variant="contained"
              sx={{ bgcolor: sa.color.pink, '&:hover': { bgcolor: sa.color.pink } }}
            >
              {clearing ? <CircularProgress size={18} color="inherit" /> : clearCfg?.action || 'Clear'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unsaved sync-settings guard */}
        <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)} maxWidth="xs">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            <WarningAmber sx={{ color: sa.color.pink }} />
            Discard unsaved sync settings?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: sa.color.text }}>
              Your sync setting changes haven&apos;t been saved. If you close now they&apos;ll be lost and the
              school keeps its current settings.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDiscard(false)} sx={{ color: sa.color.textGray }}>
              Keep editing
            </Button>
            <Button
              onClick={discardAndClose}
              variant="contained"
              sx={{ bgcolor: sa.color.pink, '&:hover': { bgcolor: sa.color.pink } }}
            >
              Discard
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
};

export default WondeManageModal;
